import { useContext, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { httpStatuses, returnStatuses, useFetch } from '@reveni-io/reveni-ui'

import AppContext from '@/context/AppContext'
import { getOrderAmount } from '@/helpers'
import { events, sendEvent } from '@/helpers/mixpanel'
import userEvents from '@/helpers/userEvents/userEvents'
import routes from '@/Router/routes.constants'
import services from '@/services/services'
import { getCompleteReturnPayload } from '@/SummaryPage/helpers'

import { checkReturnInfoMaxRetries, checkReturnInfoTimeInMs, completingMessages } from './constants'
import { shouldWaitForCheckoutUrl, shouldWaitForReturnLabels } from './helpers'

const useCompleteReturn = ({ handleForbiddenOnComplete }) => {
  const { context, setContext } = useContext(AppContext)
  const { fetchData, loading: completingReturn } = useFetch(services.completeReturn, {
    doInitialRequest: false,
  })
  const { fetchData: getReturn } = useFetch(services.getReturn, {
    doInitialRequest: false,
  })
  const navigate = useNavigate()
  const checkingReturnInfoInterval = useRef(null)
  const [checkingReturnInfo, setCheckingReturnInfo] = useState(false)
  const currentCheckReturnInfoRetries = useRef(0)

  const checkReturnInfo = async () => {
    const data = await getReturn({ returnId: context.orderInfo.returnId, orderId: context.orderInfo.id })

    const isCheckOutUrlReady = data.exchanged_for_order?.checkout_url || data.status === returnStatuses.cancelled
    const isReturnLabelsReady = data.return_labels?.length === context.orderInfo.numberOfReturnLabels

    setContext(context => ({
      ...context,
      orderInfo: {
        ...context.orderInfo,
        exchanged_for_order: data.exchanged_for_order,
        checkoutUrl: data.exchanged_for_order?.checkout_url,
        status: data.status,
        returnLabels: data.return_labels,
      },
    }))

    if (isCheckOutUrlReady && isReturnLabelsReady) {
      setCheckingReturnInfo(false)
      clearInterval(checkingReturnInfoInterval.current)
      navigate(routes.processCompletedPage)
    }
  }

  const initIntervalToWaitForCheckInfo = () => {
    setCheckingReturnInfo(true)
    checkingReturnInfoInterval.current = setInterval(() => {
      currentCheckReturnInfoRetries.current = currentCheckReturnInfoRetries.current + 1

      if (currentCheckReturnInfoRetries.current <= checkReturnInfoMaxRetries) {
        checkReturnInfo()
      } else {
        clearInterval(checkingReturnInfoInterval.current)
        navigate(routes.processCompletedPage)
      }
    }, checkReturnInfoTimeInMs)
  }

  const shouldGoToProcessCompletedPage = ({
    needsPayment,
    checkoutUrl,
    hasCarrierConnected,
    returnLabels,
    requestedReturnLabels,
  }) => {
    if (
      shouldWaitForCheckoutUrl(needsPayment, checkoutUrl) ||
      shouldWaitForReturnLabels(
        hasCarrierConnected,
        returnLabels,
        requestedReturnLabels,
        context.merchantInfo.disable_return_label_delay
      )
    ) {
      initIntervalToWaitForCheckInfo()
    } else {
      navigate(routes.processCompletedPage)
    }
  }

  const completeReturn = async (paymentMethodId, refundType) => {
    userEvents.sendEvents()
    try {
      const data = await fetchData({
        returnId: context.orderInfo.returnId,
        orderId: context.orderInfo.id,
        payload: getCompleteReturnPayload(refundType, paymentMethodId),
      })

      setContext(context => ({
        ...context,
        orderAmount: getOrderAmount(data),
        orderInfo: {
          ...context.orderInfo,
          allowed_refund_methods: data.allowed_refund_methods,
          transaction_type: data.transaction_type,
          success_url: data.success_url,
          reject_url: data.reject_url,
          discountCode: data.discount_code,
          exchanged_for_order: data.exchanged_for_order,
          refundType,
          customers_url: data.customers_url,
          returnLabels: data.return_labels,
          status: data.status,
          refund_on_hold: data.refund_on_hold,
        },
      }))

      sendEvent(events.returnCompleted, { transaction_type: data.transaction_type })

      shouldGoToProcessCompletedPage({
        needsPayment: data.exchanged_for_order?.needs_payment,
        checkoutUrl: data.exchanged_for_order?.checkout_url,
        hasCarrierConnected: context.orderInfo.deliveryMethod?.carrier_service?.is_connected,
        returnLabels: data.return_labels,
        requestedReturnLabels: context.orderInfo.numberOfReturnLabels,
      })
    } catch (err) {
      if (err.response?.status === httpStatuses.forbidden) handleForbiddenOnComplete(err.response.data.code)
    }
  }

  return {
    completeReturn,
    completingReturn: completingReturn || checkingReturnInfo,
    completingReturnMessage: completingMessages[currentCheckReturnInfoRetries.current],
  }
}

export default useCompleteReturn
